import { Response } from 'datx-jsonapi';
import { FC, useEffect, useMemo, useState } from 'react';

import { FilterCombobox } from '@/components/ui/features/ hubs-conferences/EventsFilter/components/FilterCombobox';
import { useLibraryFilters } from '@/components/ui/features/library/LibraryFilter/LibraryFilter.hooks';
import { useAnalytics } from '@/context/Analytics';
import { LibraryFilter } from '@/enums/LibraryFilter';
import { useQueryParams } from '@/hooks/useQueryParams';
import { IAlgoliaFilterItem } from '@/interfaces/IAlgoliaFilterItem';
import { IMetaOmnisearchProps } from '@/interfaces/search';
import { useResource, useResourceRelationship, useResourcesInfinite } from '@/lib/datx-jsonapi-react';
import { queryEventFiltersInfinite, querySelectedConferenceFilters } from '@/queries/algolia-filter-item';
import { getHubBySlug, getHubsSubtree } from '@/queries/hubs';
import { AlgoliaFilterItem } from '@/resources/AlgoliaFilterItem';
import { extractSWRInfiniteData } from '@/utils/swr/extractSWRInfiniteData';
import { StringParam } from '@/utils/url-query-string';

const defaultPublisher: IAlgoliaFilterItem = { label: 'All publishers', value: null, amount: 0 };

export const FilterPublisher: FC = () => {
  const analytics = useAnalytics();
  const [publisherSearch, setPublisherSearch] = useState(null);

  // get value of currently selected conference
  const { filters, change } = useLibraryFilters({
    [LibraryFilter.Publisher]: StringParam,
  });

  const { slug, query } = useQueryParams<{ query: string; slug: string }>({
    slug: StringParam,
    query: StringParam,
  });

  const { data: hub } = useResource(getHubBySlug(slug));
  const {
    data: { subtreeIds },
  } = useResourceRelationship(getHubsSubtree(hub.id));

  const { data: selectedPublisher } = useResource(
    querySelectedConferenceFilters({
      selected_resource: 'conference',
      selected_id: filters?.[LibraryFilter.Publisher],
    }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const { data, error, size, setSize } = useResourcesInfinite(
    queryEventFiltersInfinite('publishers', {
      filters: {
        query,
        publishers_query: publisherSearch,
        subject_id: subtreeIds,
      },
    })
  );

  const { items, isLoadingMore } = useMemo(
    () => extractSWRInfiniteData<AlgoliaFilterItem>(data as Array<Response<AlgoliaFilterItem>>, error, size),
    [data, error, size]
  );

  const isReachingEnd = useMemo(() => {
    if (!data) return false;
    const lastPage = data?.[data.length - 1].meta as IMetaOmnisearchProps;

    return lastPage.currentPage >= lastPage.totalPages;
  }, [data]);

  const onChange = (item) => {
    change({ [LibraryFilter.Publisher]: item?.value }, { method: 'replace', shallow: true, scroll: true });
    analytics.track('library_publisher_filter', { publisher_name: item?.title, publisher_id: item?.value });
  };

  return (
    <FilterCombobox
      placeholder="Search"
      itemsList={[defaultPublisher, ...items]}
      selectedItem={selectedPublisher || defaultPublisher}
      onChangeItem={(item) => onChange(item)}
      isLoadingMore={isLoadingMore && !isReachingEnd}
      onScrollToBottom={() => {
        if (!error && !isReachingEnd) {
          setSize(size + 1);
        }
      }}
      onSearchChange={(value) => setPublisherSearch(value)}
    />
  );
};
